import type { Answer } from '../lib/api'

export type Answers = Record<string, Answer>

/**
 * The branch a run is on, decided by the first question. It swaps question
 * wording, option sets, and which questions get asked at all.
 */
export type Branch = 'partner' | 'date' | 'friend' | 'unsure'

export function branchOf(answers: Answers): Branch {
  const context = answers.connection_context
  if (context === 'partner') return 'partner'
  if (context === 'date' || context === 'new_person') return 'date'
  if (context === 'friend') return 'friend'
  return 'unsure'
}

/** Illustration/art slot, resolved to a real asset in `funnel/art.ts`. */
export type ArtRef = string

interface BaseNode {
  id: string
  /**
   * Whether this node appears for a given set of answers. Absent means always.
   * Evaluated left-to-right over the declared order, so a predicate may only
   * depend on answers to *earlier* nodes.
   */
  when?: (answers: Answers, flags: FlowFlags) => boolean
  art?: ArtRef
}

export interface QuestionNode extends BaseNode {
  kind: 'question'
  /** Server-side key. Usually equals `id`, but branch variants share one key. */
  questionKey: string
  select: 'single' | 'multi' | 'grid'
  optionsFor: (answers: Answers) => string[]
  /** preferred_depth is stored numerically; everything else stays a string. */
  coerce?: (value: string) => Answer
  /** In a multi-select, choosing this clears every other choice. */
  exclusiveOption?: string
  /** Multi-selects can be submitted empty ("nothing to leave out"). */
  optional?: boolean
}

export interface ValueNode extends BaseNode {
  kind: 'value' | 'insight' | 'howitworks'
  /** Picks which copy variant to show, e.g. reassurance keyed to their barrier. */
  variantFor?: (answers: Answers) => string
  /**
   * A composed visual device instead of a stock illustration. These are built
   * from the product or from the visitor's own answers — never from invented
   * statistics, which the copy guardrails forbid.
   */
  visual?: 'compare' | 'prompt-pass' | 'pace-meter'
}

/** The hero screen. Sits before the first question and owns the bare `/` URL. */
export interface LandingNode extends BaseNode {
  kind: 'landing'
}

export interface TerminalNode extends BaseNode {
  kind: 'crafting' | 'gate' | 'result' | 'confirm' | 'offer' | 'handoff'
}

export type FlowNode = LandingNode | QuestionNode | ValueNode | TerminalNode

/** Run-level switches that change the path but aren't themselves answers. */
export interface FlowFlags {
  /** Research participation — unticked means those questions never appear. */
  research: boolean
  /**
   * Selling mode, from the server. Decides the screen after the result: the
   * waitlist confirmation, or the Premium Pass offer. Exactly one of the two is
   * ever on a path, so the visitor is never asked to both wait and buy.
   */
  mode: 'waitlist' | 'paid'
}

export const defaultFlags: FlowFlags = { research: false, mode: 'paid' }

const asString = (value: Answer | undefined): string => (typeof value === 'string' ? value : '')

// ---------------------------------------------------------------------------
// The graph. Declaration order is flow order; `when` decides who sees what.
// ---------------------------------------------------------------------------

export const NODES: FlowNode[] = [
  // 0 — Hero. A cold visitor from a TikTok ad has never heard of Bonded, so the
  // funnel opens by saying what it is before it asks for anything. Mirrors the
  // bonded.studio/app hero: pill, headline, illustration, one CTA.
  { id: 'landing', kind: 'landing', art: 'hands-holding' },

  // 1 — First question, immediately after the hero.
  {
    id: 'connection_context',
    kind: 'question',
    questionKey: 'connection_context',
    select: 'grid',
    optionsFor: () => ['partner', 'date', 'friend', 'new_person', 'unsure'],
    art: 'landing',
  },

  // 2 — Replaces the old standalone 18+ gate. One question that both confirms
  // adulthood and yields a real age band (the server derives adult_confirmation).
  {
    id: 'age_band',
    kind: 'question',
    questionKey: 'age_band',
    select: 'grid',
    optionsFor: () => ['18_24', '25_34', '35_44', '45_54', '55_64', '65_plus', 'under_18'],
  },

  { id: 'right_question', kind: 'value', visual: 'compare' },

  // 4 — Branch-specific. Three variants of "how long / how far along", one key
  // each so the answers stay analysable per branch.
  {
    id: 'together_length',
    kind: 'question',
    questionKey: 'together_length',
    select: 'single',
    when: (answers) => branchOf(answers) === 'partner',
    optionsFor: () => ['under_1y', '1_3y', '3_7y', 'over_7y'],
  },
  {
    id: 'dating_stage',
    kind: 'question',
    questionKey: 'dating_stage',
    select: 'single',
    when: (answers) => branchOf(answers) === 'date',
    optionsFor: () => ['not_met_yet', 'first_date_soon', 'few_dates_in', 'just_started_seeing'],
  },
  {
    id: 'friend_length',
    kind: 'question',
    questionKey: 'friend_length',
    select: 'single',
    when: (answers) => branchOf(answers) === 'friend',
    optionsFor: () => ['new_friend', 'few_years', 'since_forever'],
  },

  {
    id: 'connection_goal',
    kind: 'question',
    questionKey: 'connection_goal',
    select: 'single',
    optionsFor: () => ['fun', 'closeness', 'understanding', 'growth'],
  },

  // 6 — First payoff: their goal, drawn back at them as a chart. This is the
  // earliest point the funnel gives something instead of taking.
  { id: 'insight', kind: 'insight' },

  {
    id: 'main_barrier',
    kind: 'question',
    questionKey: 'main_barrier',
    select: 'single',
    when: (_answers, flags) => flags.research,
    optionsFor: () => ['questions', 'distraction', 'time_energy', 'repetition', 'awkward_vulnerability', 'none'],
  },

  // 8 — Branch-specific pain probe. The sharpest avatar data in the funnel.
  {
    id: 'last_real_conversation',
    kind: 'question',
    questionKey: 'last_real_conversation',
    select: 'single',
    when: (answers) => branchOf(answers) === 'partner',
    optionsFor: () => ['this_week', 'this_month', 'cant_remember', 'we_talk_like_this_often'],
  },
  {
    id: 'first_date_worry',
    kind: 'question',
    questionKey: 'first_date_worry',
    select: 'single',
    when: (answers) => branchOf(answers) === 'date',
    optionsFor: () => ['running_out_of_things', 'feels_like_interview', 'being_too_much', 'nothing_really'],
  },
  {
    id: 'catchup_pattern',
    kind: 'question',
    questionKey: 'catchup_pattern',
    select: 'single',
    when: (answers) => branchOf(answers) === 'friend',
    optionsFor: () => ['same_topics', 'we_go_deep', 'depends'],
  },

  // 9 — Answers the barrier they just named, in their own terms. Falls back to
  // a general reframe for the runs that skipped the barrier question.
  {
    id: 'reassurance',
    kind: 'value',
    visual: 'prompt-pass',
    variantFor: (answers) => {
      const barrier = asString(answers.main_barrier)
      if (barrier === 'awkward_vulnerability') return 'comfort'
      if (barrier === 'questions') return 'we_bring_questions'
      if (barrier === 'distraction' || barrier === 'time_energy') return 'ten_minutes'
      if (barrier === 'repetition') return 'never_the_same'
      // No barrier answer (research declined, or "nothing in particular").
      if (asString(answers.first_date_worry) === 'feels_like_interview') return 'not_an_interview'
      if (asString(answers.last_real_conversation) === 'cant_remember') return 'ten_minutes'
      if (asString(answers.catchup_pattern) === 'same_topics') return 'never_the_same'
      return 'general'
    },
  },

  {
    id: 'intentional_time_frequency',
    kind: 'question',
    questionKey: 'intentional_time_frequency',
    select: 'single',
    when: (_answers, flags) => flags.research,
    optionsFor: () => ['daily', 'weekly', 'monthly', 'rarely', 'first_time'],
  },

  // 11-12 — The only place a cold visitor sees what the product actually is.
  { id: 'howitworks_together', kind: 'howitworks', art: 'app-home' },
  { id: 'howitworks_moment', kind: 'howitworks', art: 'app-experiences' },

  {
    id: 'social_energy',
    kind: 'question',
    questionKey: 'social_energy',
    select: 'single',
    optionsFor: () => ['soft', 'curious', 'playful', 'bold'],
  },
  {
    id: 'intended_setting',
    kind: 'question',
    questionKey: 'intended_setting',
    select: 'single',
    optionsFor: () => ['home', 'date', 'travelling_walking', 'friends', 'later'],
  },
  {
    id: 'available_time',
    kind: 'question',
    questionKey: 'available_time',
    select: 'single',
    optionsFor: () => ['5_10', '15_20', '30_45', 'flexible'],
  },
  {
    id: 'preferred_depth',
    kind: 'question',
    questionKey: 'preferred_depth',
    select: 'single',
    optionsFor: () => ['1', '2', '3', '4'],
    coerce: (value) => Number(value),
  },
  {
    id: 'topic_boundaries',
    kind: 'question',
    questionKey: 'topic_boundaries',
    select: 'multi',
    optional: true,
    exclusiveOption: 'prefer_not_to_say',
    optionsFor: () => ['exes', 'money', 'family', 'future', 'sex_intimacy', 'religion', 'politics', 'prefer_not_to_say'],
  },

  { id: 'your_pace', kind: 'value', visual: 'pace-meter' },

  {
    id: 'scan_trigger',
    kind: 'question',
    questionKey: 'scan_trigger',
    select: 'single',
    when: (_answers, flags) => flags.research,
    optionsFor: () => ['social_video', 'friend_told_me', 'card', 'time_together', 'reconnecting', 'repeated_conversation', 'first_meeting', 'curiosity'],
  },
  {
    id: 'return_value',
    kind: 'question',
    questionKey: 'return_value',
    select: 'single',
    when: (_answers, flags) => flags.research,
    optionsFor: () => ['experiences', 'guided_rituals', 'memory_capsules', 'insights', 'unlimited_hosting'],
  },

  { id: 'crafting', kind: 'crafting' },
  { id: 'gate', kind: 'gate' },
  { id: 'result', kind: 'result' },
  // Mutually exclusive by mode — never both.
  { id: 'confirm', kind: 'confirm', when: (_answers, flags) => flags.mode === 'waitlist' },
  { id: 'offer', kind: 'offer', when: (_answers, flags) => flags.mode === 'paid' },
  { id: 'handoff', kind: 'handoff' },
]

const BY_ID = new Map(NODES.map((node) => [node.id, node]))

export function nodeById(id: string): FlowNode | undefined {
  return BY_ID.get(id)
}

/** The nodes this particular run will actually see, in order. */
export function resolvePath(answers: Answers, flags: FlowFlags = defaultFlags): FlowNode[] {
  return NODES.filter((node) => !node.when || node.when(answers, flags))
}

export const firstNodeId = NODES[0].id

/**
 * The next node after `currentId` on this run's path. Falls through to the
 * crafting screen if the current node isn't on the path — a stale bookmark or a
 * branch that changed underneath the user must never dead-end.
 */
export function nextNodeId(currentId: string, answers: Answers, flags: FlowFlags = defaultFlags): string {
  const path = resolvePath(answers, flags)
  const index = path.findIndex((node) => node.id === currentId)
  if (index < 0 || index === path.length - 1) return 'crafting'
  return path[index + 1].id
}

/** The node before `currentId`, or null at the start of the funnel. */
export function previousNodeId(currentId: string, answers: Answers, flags: FlowFlags = defaultFlags): string | null {
  const path = resolvePath(answers, flags)
  const index = path.findIndex((node) => node.id === currentId)
  return index > 0 ? path[index - 1].id : null
}

/** Screens after the questions end — excluded from the progress denominator. */
const POST_QUESTION_KINDS = new Set(['landing', 'crafting', 'gate', 'result', 'confirm', 'offer', 'handoff'])

/**
 * Progress through the answering part of the funnel, 0..1.
 *
 * Measured over the *resolved* path so the bar reflects the questions this
 * person will really be asked. Because a branch can shorten the path mid-run,
 * callers clamp this monotonically (see `useMonotonicProgress`) — a bar that
 * jumps backwards reads as "I'm losing ground" and costs completions.
 */
export function progressFor(currentId: string, answers: Answers, flags: FlowFlags = defaultFlags): number {
  const path = resolvePath(answers, flags).filter((node) => !POST_QUESTION_KINDS.has(node.kind))
  const index = path.findIndex((node) => node.id === currentId)
  if (index < 0) return 1
  return (index + 1) / path.length
}

/** "Question 4 of 11" — counts only real questions, not value screens. */
export function stepNumberFor(currentId: string, answers: Answers, flags: FlowFlags = defaultFlags): { n: number; total: number } {
  const questions = resolvePath(answers, flags).filter((node) => node.kind === 'question')
  const index = questions.findIndex((node) => node.id === currentId)
  return { n: index + 1, total: questions.length }
}

/**
 * Where to resume someone who lands mid-funnel: the first node on their path
 * that has no answer yet. Questions already answered are skipped, so a refresh
 * or a returning session picks up exactly where it left off.
 */
export function firstUnansweredNodeId(answers: Answers, flags: FlowFlags = defaultFlags): string {
  const path = resolvePath(answers, flags)
  for (const node of path) {
    if (node.kind !== 'question') continue
    if (answers[node.questionKey] === undefined) return node.id
  }
  return 'crafting'
}

/** Whether every question required to build a result has been answered. */
export const REQUIRED_KEYS = ['connection_context', 'connection_goal', 'social_energy', 'available_time', 'preferred_depth'] as const

export function canBuildResult(answers: Answers): boolean {
  return REQUIRED_KEYS.every((key) => answers[key] !== undefined) && answers.adult_confirmation !== false
}
